import React, { useState, useEffect } from 'react';
import { mockProducts, subscribeToDB } from '../services/mockDatabase';
import { Settings } from 'lucide-react';

const Product = ({ onAddToCart }) => {
  const [productsList, setProductsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    setProductsList(mockProducts.getProducts());
    const unsubscribe = subscribeToDB(() => {
      setProductsList(mockProducts.getProducts());
    });
    return unsubscribe;
  }, []);

  // Extract unique categories
  const categories = ['All', ...new Set(productsList.map(p => p.category).filter(Boolean))];

  const filteredProducts = selectedCategory === 'All'
    ? productsList
    : productsList.filter(p => p.category === selectedCategory);

  return (
    <section id="shop" className="py-16 px-6 bg-white">
      <h3 className="text-3xl font-bold text-center text-gray-800 mb-4">Georgia's Favorites</h3>
      <p className="text-center text-gray-500 max-w-md mx-auto mb-10">
        Browse hand-crafted bead designs. Select customizable items to add custom names or custom color selections.
      </p>

      {/* Category Pills */}
      <div className="flex justify-center gap-2 mb-12 flex-wrap">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
              selectedCategory === cat
                ? 'bg-pink-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:text-pink-600'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {filteredProducts.map(product => (
          <div 
            key={product.id}
            className="group border-2 border-gray-100 rounded-3xl p-5 text-center hover:border-pink-200 hover:shadow-xl transition-all duration-300 flex flex-col bg-pink-50/20"
          >
            {/* Image Placeholder or Image */}
            <div className="relative rounded-2xl h-60 mb-5 overflow-hidden bg-gray-100 flex items-center justify-center text-gray-400 italic">
              {product.imageUrl ? (
                <img 
                  src={product.imageUrl} 
                  alt={product.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '';
                  }}
                />
              ) : (
                <span>[ {product.name} Image ]</span>
              )}
              {product.isCustomizable && (
                <span className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Settings size={12} /> Customizable
                </span>
              )}
            </div>

            <h4 className="text-xl font-bold text-gray-800 mb-1">{product.name}</h4>
            <p className="text-gray-500 text-sm mb-4 line-clamp-2 min-h-[40px]">{product.description}</p>
            <p className="text-pink-600 font-extrabold text-2xl mb-6">${product.price.toFixed(2)}</p>

            <div className="mt-auto flex gap-2">
              {product.isCustomizable ? (
                <a 
                  href={`#/customize/${product.id}`}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 rounded-xl hover:shadow-lg transition text-center text-sm"
                >
                  Customize & Add
                </a>
              ) : (
                <button 
                  onClick={() => onAddToCart({ ...product, quantity: 1 })}
                  className="flex-1 bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl hover:shadow-lg transition text-sm"
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>
        ))}

        {filteredProducts.length === 0 && (
          <p className="text-center text-gray-400 col-span-3 italic py-10">No items available in this category.</p>
        )}
      </div>
    </section>
  );
};

export default Product;
